<template>
  <div class="profile-spouse-rel">
    <div class="card-area">
      <SpouseConnector/>
      <ProfileRelationshipCard :rel-person-id="viewSpouseIdRef" :context-person-id="personId" :is-rel-owner="isRelOwnerRef" :owner-only="ownerOnly"/>
    </div>
    <div v-if="canEditRef" class="action-row">
      <button type="button" class="btn btn-inline btn-link" @click="onEdit">
        Change
      </button>
      <button type="button" class="btn btn-inline btn-link" @click="onRemove">
        Remove
      </button>
    </div>
    <div class="owners-row">
      <button type="button" v-if="!props.ownerOnly && hasOtherOwnersRef" class="btn btn-inline btn-link link-secondary" @click="showOwnersModal">
        Relationship defined in <span class="count"> ({{ relOwnerProfileIdsRef.length }})</span>
      </button>
    </div>
    <ProfileChildren
      :personId="props.personId"
      :family="props.spouseFamily"
      @add-child="emit('addChild')">
    </ProfileChildren>
    <ProfileRelationshipOwnersModal ref="ownersModalRef" spouse
      :person-id="personId"
      :owner-profile-ids="relOwnerProfileIdsRef"/>
    <Modal ref="messageModalRef" info>
      <template #title>Remove spouse</template>
      {{ messageModalTextRef }}
    </Modal>
    <Modal ref="multipleDefsModalRef" @ok="onMultipleDefsConfirmed">
      <template #title>Remove spouse</template>
      This spouse relationship is also defined in another profile.
      You can remove the relationship from this profile, but it will still be visible to anyone who can see both profiles.
      <template #okText>Continue</template>
    </Modal>
    <ProfileSpouseRelationshipModal ref="relationshipModalRef"/>
  </div>
</template>

<style lang="scss" scoped>
.profile-spouse-rel {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.card-area {
  display: flex;
  align-items: start;

  .spouse-connector {
    margin-top: 20px;
  }
}

.count {
  font-size: 0.75rem;
  color: #aaa;
}

.action-row {
  margin-left: 2rem;
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
}

.owners-row {
  margin-left: 2rem;
  font-size: 0.75rem;
}

.link-secondary {
  color: #888;
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { FamilyAndChildren } from '@/rd/ViewFamilyStore'
import { CompositeId } from '@/rd/CompositeId'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { ProfileUtils } from './ProfileUtils'
import { useFamilyStore } from '@/rd/FamilyStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { FamilyRelationshipRole } from '@/rd/ResearchDataModel'
import { hideModalAsync } from '@/util/AppUtil'
import Modal from '@/util/Modal.vue'
import ProfileChildren from './ProfileChildren.vue'
import ProfileSpouseRelationshipModal from './ProfileSpouseRelationshipModal.vue'
import SpouseConnector from '@/manage/SpouseConnector.vue'
import ProfileRelationshipCard from './ProfileRelationshipCard.vue'
import ProfileRelationshipOwnersModal from './ProfileRelationshipOwnersModal.vue'

const props = defineProps<{
  personId: string
  spouseFamily: FamilyAndChildren
  ownerOnly?: boolean
}>()

const emit = defineEmits([
  "addChild",
  "remove",
])

const messageModalTextRef = ref('')
const ownersModalRef = ref<InstanceType<typeof ProfileRelationshipOwnersModal>>()

const viewPersonStore = useViewPersonStore()
const familyStore = useFamilyStore()
const dataGroupStore = useDataGroupStore()
const assertionStore = useAssertionStore()

const relationshipModalRef = ref<InstanceType<typeof ProfileSpouseRelationshipModal>>()
const messageModalRef = ref<InstanceType<typeof Modal>>()
const multipleDefsModalRef = ref<InstanceType<typeof Modal>>()

const profileGroupIdRef = computed(() => CompositeId.getGroupId(props.personId)!)
const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const viewSpouseIdRef = computed(() => props.spouseFamily.family.spouseOf(viewPersonRef.value?.id ?? ''))
const personRoleRef = computed(() => props.spouseFamily.family.roleOf(viewPersonRef.value?.id ?? ''))
const spouseRoleRef = computed(() => personRoleRef.value == FamilyRelationshipRole.Father ? FamilyRelationshipRole.Mother : FamilyRelationshipRole.Father)
const relOwnerProfileIdsRef = computed(() => {
  // spouse relationship owners are the person that owns each family match:
  // - for a family in a profile group: the group's primary person (may be a spouse, child, or other)
  // - for a family in a research group: the father (or mother if no father)
  const families = familyStore.getLoadedFamilyList(props.spouseFamily.family.matchIds)
  const dataGroups = dataGroupStore.getLoadedGroupList(props.spouseFamily.family.groupIds)
  return ProfileUtils.getSpouseFamilyProfileIds(families, dataGroups)
})
const isRelOwnerRef = computed(() => relOwnerProfileIdsRef.value.includes(props.personId))
const hasOtherOwnersRef = computed(() => relOwnerProfileIdsRef.value.some(id => id !== props.personId))
const permissionsRef = computed(() => dataGroupStore.getLoadedPermissions(profileGroupIdRef.value))
const canEditPersonRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)
const canEditRef = computed(() => canEditPersonRef.value && relOwnerProfileIdsRef.value.includes(props.personId))
const familyAssertionsRef = computed(() => assertionStore.getLoadedForFamily(props.spouseFamily.family.id!))

function showOwnersModal() {
  ownersModalRef.value?.open()
}

function onEdit() {
  relationshipModalRef.value?.open(profileGroupIdRef.value, props.spouseFamily.family, spouseRoleRef.value)
}

function onRemove() {
  if (props.spouseFamily.childIds.length) {
    messageModalTextRef.value = 
      `This spouse family still has ${props.spouseFamily.childIds.length} children. All children must be removed before you can delete the family.`
    messageModalRef.value?.open()
  }
  else if (familyAssertionsRef.value.length) {
    messageModalTextRef.value = 
      `This spouse family still has ${familyAssertionsRef.value.length} details defined. All family details must be removed before you can delete the family.`
    messageModalRef.value?.open()
  }
  else if (props.spouseFamily.family.matchIds.length > 1) {
    multipleDefsModalRef.value?.open()
  }
  else {
    emit('remove', props.spouseFamily)
  }
}

async function onMultipleDefsConfirmed() {
  if (multipleDefsModalRef.value) {
    await hideModalAsync(multipleDefsModalRef.value!.$el)
  }
  emit('remove', props.spouseFamily)
}

</script>
